"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InteractiveDomHoCDecorator = exports.InteractiveDomHoC = exports.InteractiveDomWrapper = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var GlobalPointerUpListener_1 = require("./GlobalPointerUpListener");
var PointerEvents_1 = require("./PointerEvents");
var ReactHelpers_1 = require("./ReactHelpers");
function reactElementIsDomElement(type) {
    // per JSX API, when `type` is a string that starts with a lower-case letter, it's a DOM element
    // maybe not all children are JSX elements, but checking that the type is a string rather than a symbol is a safe-enough assumption
    // https://reactjs.org/docs/jsx-in-depth.html#user-defined-components-must-be-capitalized
    return typeof type === 'string' && type.charAt(0) === type.charAt(0).toLowerCase();
}
/**
 * This returns a combined styling with style as it's base. Depending upon the state of the component it assigns
 * onto it the hover, focus, and active styling while overwriting in that order. If disabled, the
 * hover/active/focus styles do not get assigned, but the disabled styling gets assigned.
 */
function resolveInteractiveStyles(style, component) {
    if (style === void 0) { style = {}; }
    var _a = component.props, disabled = _a.disabled, interactiveStyles = _a.interactiveStyles;
    var _b = component.state, hover = _b.hover, active = _b.active, focus = _b.focus, focusVisible = _b.focusVisible;
    if (!interactiveStyles) {
        return style;
    }
    return tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, style), (hover && !disabled && interactiveStyles.hover)), (focus && !disabled && interactiveStyles.focus)), (focus && focusVisible && !disabled && interactiveStyles.focusVisible)), (active && !disabled && interactiveStyles.active)), (disabled && interactiveStyles.disabled));
}
/**
 * We must add the pointerUp styling events to the window instead of the element so that the
 * user can mouse down on the element, move off it, mouseUp, and the element can be
 * made to have inactive styling.
 */
var attachPointerUpListener = function (component) {
    var handlePointerUp = function () {
        var _a;
        if (!component.interactiveIsMounted) {
            return;
        }
        component.setState({ active: false, focusVisible: false });
        if (component.props.handleActive) {
            component.props.handleActive(false);
        }
        (_a = component.removePointerUpListener) === null || _a === void 0 ? void 0 : _a.call(component);
        component.removePointerUpListener = undefined;
    };
    component.removePointerUpListener = GlobalPointerUpListener_1.GlobalPointerUpListener.subscribe(handlePointerUp);
};
function renderInteractiveComponent(component, domElement) {
    var _a = component.props, disabled = _a.disabled, pointerDown = _a.pointerDown;
    var pointerEventProps = disabled ? {} : {
        pointerDown: ReactHelpers_1.mergeEventHandlers(pointerDown, function () {
            if (!component.interactiveIsMounted) {
                return;
            }
            component.setState({ active: true });
            if (component.props.handleActive) {
                component.props.handleActive(true);
            }
            if (!component.removePointerUpListener) {
                attachPointerUpListener(component);
            }
        })
    };
    // hack to reach down another layer in the situation that multiple HOCs are stacked
    var childProps = reactElementIsDomElement(domElement.type) || !domElement.props.children ?
        domElement.props :
        React.Children.only(domElement.props.children).props;
    var onMouseOver = childProps.onMouseOver, onMouseOut = childProps.onMouseOut, onKeyDown = childProps.onKeyDown, onKeyUp = childProps.onKeyUp, onFocus = childProps.onFocus, onBlur = childProps.onBlur;
    var domEventProps = disabled ? {} : {
        onMouseOver: ReactHelpers_1.mergeEventHandlers(onMouseOver, function () {
            if (!component.interactiveIsMounted) {
                return;
            }
            component.setState({ hover: true });
            if (component.props.handleHover) {
                component.props.handleHover(true);
            }
        }),
        onMouseOut: ReactHelpers_1.mergeEventHandlers(onMouseOut, function () {
            if (!component.interactiveIsMounted) {
                return;
            }
            component.setState({ hover: false });
            if (component.props.handleHover) {
                component.props.handleHover(false);
            }
        }),
        onKeyDown: ReactHelpers_1.mergeEventHandlers(onKeyDown, function (e) {
            if (!component.interactiveIsMounted) {
                return;
            }
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
                component.setState({ active: true, focus: true, focusVisible: true });
                if (component.props.handleActive) {
                    component.props.handleActive(true);
                }
            }
            else {
                component.setState({ focus: true, focusVisible: true });
            }
        }),
        onKeyUp: ReactHelpers_1.mergeEventHandlers(onKeyUp, function (e) {
            if (!component.interactiveIsMounted) {
                return;
            }
            if (e.key === ' ' || e.key === 'Enter') {
                component.setState({ active: false, focus: true, focusVisible: true });
                if (component.props.handleActive) {
                    component.props.handleActive(false);
                }
            }
            else {
                component.setState({ focus: true, focusVisible: true });
            }
        }),
        onFocus: ReactHelpers_1.mergeEventHandlers(onFocus, function () {
            if (!component.interactiveIsMounted) {
                return;
            }
            component.setState({ focus: true });
            if (component.props.handleFocus) {
                component.props.handleFocus(true);
            }
        }),
        onBlur: ReactHelpers_1.mergeEventHandlers(onBlur, function () {
            if (!component.interactiveIsMounted) {
                return;
            }
            component.setState({ focus: false, focusVisible: false });
            if (component.props.handleFocus) {
                component.props.handleFocus(false);
            }
        }),
    };
    var resolvedStyles = resolveInteractiveStyles(domElement.props.style, component);
    var resolvedProps = tslib_1.__assign({ style: resolvedStyles, disabled: disabled }, domEventProps);
    var resolvedPointerEventProps = tslib_1.__assign(tslib_1.__assign({}, component.props), pointerEventProps);
    return (React.createElement(PointerEvents_1.PointerEventWrapper, tslib_1.__assign({}, resolvedPointerEventProps), React.cloneElement(domElement, resolvedProps)));
}
/**
 * Wrap a DOM component to give it interactive styles and pointer events. All events from the PointerEventWrapper can also be used here.
 *
 * When using, you can only have one child and it must render its props to a DOM component.
 * If there is not exactly one child an exception will be thrown.
 *
 * Example that attaches a click to a div and gives different styling on hover:
 * @example
 *    const interactiveProps = {
 *      pointerClick: () => console.log('click'),
 *      interactiveStyles: {
 *        hover: { color: 'salmon' }
 *      }
 *    };
 *
 *    // DOM attributes should go straight to the DOM element
 *    const domProps = {
 *      style: { color: 'fallow' },
 *      onBlur: () => console.log('blur')
 *    };
 *
 *    <InteractiveDomWrapper {...props}>
 *      <div {...domProps} />
 *    </InteractiveDomWrapper>
 */
var InteractiveDomWrapper = /** @class */ (function (_super) {
    tslib_1.__extends(InteractiveDomWrapper, _super);
    function InteractiveDomWrapper(props) {
        var _this = _super.call(this, props) || this;
        _this.state = tslib_1.__assign(tslib_1.__assign({}, _this.state), { hover: false, active: false, focus: false, focusVisible: false });
        _this.interactiveIsMounted = true;
        return _this;
    }
    InteractiveDomWrapper.getDerivedStateFromProps = function (props, state) {
        if (props.disabled && (state.active || state.focus || state.hover || state.focusVisible)) {
            return {
                active: false,
                focus: false,
                focusVisible: false,
                hover: false,
            };
        }
        return null;
    };
    InteractiveDomWrapper.prototype.componentWillUnmount = function () {
        this.interactiveIsMounted = false;
        if (this.removePointerUpListener) {
            this.removePointerUpListener();
        }
    };
    InteractiveDomWrapper.prototype.render = function () {
        // reuse the child. It could be anything - div, ul, button, etc.
        return renderInteractiveComponent(this, React.Children.only(this.props.children));
    };
    return InteractiveDomWrapper;
}(React.Component));
exports.InteractiveDomWrapper = InteractiveDomWrapper;
/**
 * Higher order component providing CSS :pseudo classes behavior and pointer events. All events from the PointerEventWrapper
 * can also be used here.
 *
 * You can use this to wrap either stateless components or component classes. To use as a @Decorator,
 * use the InteractiveDomHoCDecorator
 */
var InteractiveDomHoC = function (componentType) {
    var _a;
    var componentClass = ReactHelpers_1.convertToStatefulComponentClass(componentType);
    var newComponentClass = (_a = /** @class */ (function (_super) {
            tslib_1.__extends(class_1, _super);
            function class_1(props) {
                var _this = _super.call(this, props) || this;
                _this.state = tslib_1.__assign(tslib_1.__assign({}, _this.state), { hover: false, active: false, focus: false, focusVisible: false });
                _this.interactiveIsMounted = true;
                return _this;
            }
            class_1.getDerivedStateFromProps = function (props, state) {
                if (props.disabled && (state.active || state.focus || state.hover || state.focusVisible)) {
                    return {
                        active: false,
                        focus: false,
                        focusVisible: false,
                        hover: false,
                    };
                }
                return null;
            };
            /**
             * Why no super?  React.Component does not actually implement the component lifecycle methods, but checks the instance
             * at runtime to see if they are defined.  Therefore super.componentWillUnmount property does not exist at compile time.
             * Instead we look to see if componentWillUnmount exists on the componentClass by looking at it's prototype and calling it
             * if so.
             */
            class_1.prototype.callComponentClassWillUnmount = function () {
                var componentClassWillUnmount = componentClass.prototype['componentWillUnmount'];
                if (componentClassWillUnmount) {
                    componentClassWillUnmount.apply(this);
                }
            };
            class_1.prototype.componentWillUnmount = function () {
                this.callComponentClassWillUnmount();
                this.interactiveIsMounted = false;
                if (this.removePointerUpListener) {
                    this.removePointerUpListener();
                }
            };
            class_1.prototype.render = function () {
                return renderInteractiveComponent(this, _super.prototype.render.call(this));
            };
            return class_1;
        }(componentClass)),
        _a.displayName = "InteractiveDom(" + ReactHelpers_1.getComponentName(componentType) + ")",
        _a);
    return newComponentClass;
};
exports.InteractiveDomHoC = InteractiveDomHoC;
/** Do some type magic so this can be used as a @Decorator on a component class */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
exports.InteractiveDomHoCDecorator = exports.InteractiveDomHoC;
//# sourceMappingURL=InteractiveDom.js.map